// El argumento de venta de la portada: todo lo que NO es el formulario.
//
// Vive aparte de `Login.js` por una razón práctica: Login maneja tres
// modos —entrar, registrarse, recuperar— y ya era el archivo con más
// estado de la pantalla. Meterle además el copy de marketing lo volvía
// imposible de leer, y este componente no tiene ni una sola línea de
// estado.
//
// ═══════════════════════════════════════════════════════════════════
// LO QUE DICE Y POR QUÉ ESO
// ═══════════════════════════════════════════════════════════════════
//
// El mensaje central se eligió para que se entienda sin saber qué es
// Brujel, sin saber que hay IA adentro y sin conocer la categoría:
//
//   "Fotografías tu ropa una vez y Brujel arma tus outfits con todo lo
//    que tienes, no solo con las tres prendas de siempre."
//
// Las cuatro pruebas de abajo NO son aspiracionales. Cada una nombra
// algo que está construido y desplegado hoy, y en ese orden:
//
//   1. una foto por prenda    → carga por lote + lectura con IA
//   2. arma los outfits       → pestaña "Qué me pongo", por ocasión
//   3. con todo tu placard    → orden frescura → inédita → uso
//   4. te empuja a no comprar → Comprar.js, las de comprar al final
//
// ⚠️  SI ALGUNA DE LAS CUATRO DEJA DE SER CIERTA, ESTE ARCHIVO MIENTE
//     Y NADIE SE ENTERA. No hay test que pueda comparar una promesa
//     de marketing con el comportamiento del producto. Queda escrito
//     acá porque es el único lugar donde alguien que toque esas
//     funciones lo va a leer.

import { IDIOMAS, idiomaActual, ponerIdioma, t } from "./i18n";
import "./portada.css";

/* La barra de arriba: el logotipo y el selector de idioma.
 *
 * El <h1> lleva la imagen con su `alt`, igual que antes: ese alt es lo
 * que le da nombre accesible al encabezado principal de la pantalla.
 * Si se vacía, la portada entera queda sin h1 con texto. */
export function PortadaBarra() {
  return (
    <header className="portada-barra">
      <h1>
        <img src="/logo-brujel.png" alt="Brujel" width="640" height="348" />
      </h1>

      {/* El selector va acá y NO adentro del <form>: un <select> dentro
          de un formulario que se envía con Enter es una fuente conocida
          de envíos accidentales — el alta se mandaría a medio llenar
          por cambiar de idioma con el teclado. Ahora además está en
          otro componente, así que no puede volver adentro por descuido. */}
      <select
        className="portada-idioma"
        aria-label={t("login.idioma", "Idioma")}
        value={idiomaActual()}
        onChange={(e) => ponerIdioma(e.target.value)}
      >
        {IDIOMAS.map((i) => (
          <option key={i.codigo} value={i.codigo}>
            {i.nativo}
          </option>
        ))}
      </select>
    </header>
  );
}

/* El argumento: eslogan, mensaje central y el dolor.
 *
 * El eslogan va ARRIBA del titular y en cuerpo chico a propósito. Solo
 * no dice qué hace el producto —"Ya lo tienes" puede ser cualquier
 * cosa—, así que nunca aparece sin el mensaje central debajo. */
export function PortadaArgumento() {
  return (
    <div>
      <p className="portada-eslogan">{t("portada.eslogan", "Ya lo tienes.")}</p>

      {/* <h2> y no <h1>: el h1 es el logotipo de la barra. Dos h1 en una
          página dejan a un lector de pantalla sin saber cuál es el
          encabezado principal. */}
      <h2 className="portada-titular">
        {t(
          "portada.titular",
          "Fotografías tu ropa una vez y Brujel arma tus outfits con todo lo que tienes, no solo con las tres prendas de siempre."
        )}
      </h2>

      <p className="portada-contexto">
        {t(
          "portada.contexto",
          "Tu placard es un activo que no administras. Tienes cuarenta prendas y usas tres. Las otras treinta y siete no están perdidas ni rotas: están invisibles. No las ves cuando abres la puerta a las siete de la mañana, y por eso compras una camisa que ya tenías, y una que costó lo mismo se muere con la etiqueta puesta."
        )}
      </p>
    </div>
  );
}

/* Las cuatro pruebas.
 *
 * Un <dl> y no una lista de <p>: cada una es un par afirmación →
 * evidencia, que es exactamente lo que un dl declara. Un lector de
 * pantalla anuncia el vínculo entre las dos partes; una lista de
 * párrafos lo pierde. */
/* ⚠️  CUATRO BLOQUES ESCRITOS A MANO Y NO UN `.map()` SOBRE UN ARRAY.
 *
 * La primera versión armaba `[{titulo: t(...), detalle: t(...)}, …]` y
 * lo recorría con `{p.titulo}` / `{p.detalle}`. Se ve mejor y es peor:
 * `test_valores_crudos.js` §5b lo marcó como etiqueta cruda, porque
 * desde el JSX no hay forma de saber si eso ya pasó por `t()`.
 *
 * El camino fácil era declarar la excepción, como ya hay tres. Pero el
 * propio test cuenta que una de esas tres «se escribió confiando en el
 * comentario de Perfil.js y era FALSA»: uno de los cuatro llamadores
 * pasaba la lista sin traducir y nadie lo vio durante meses.
 *
 * Un permiso más en esa lista es un lugar más donde hay que confiar.
 * Escribir las cuatro llamadas donde se dibujan cuesta doce líneas y
 * no hay que confiar en nada: las claves quedan literales, el detector
 * las ve, y `grep portada.p3` las encuentra. */
export function PortadaPruebas() {
  return (
    <section className="portada-pruebas">
      <div className="portada-pruebas-dentro">
        <h2>
          {t("portada.pruebasTitulo", "Todo esto ya está construido y funcionando")}
        </h2>
        <dl className="portada-lista">
          <div className="portada-prueba">
            <dt>{t("portada.p1", "Una foto por prenda")}</dt>
            <dd>
              {t(
                "portada.p1d",
                "Las subes por lote. Brujel lee la prenda, la marca, los colores y las ocasiones desde la foto."
              )}
            </dd>
          </div>

          <div className="portada-prueba">
            <dt>{t("portada.p2", "Arma los outfits")}</dt>
            <dd>
              {t(
                "portada.p2d",
                "Filtrados por ocasión, y ordenados por lo que hace más tiempo no usas."
              )}
            </dd>
          </div>

          <div className="portada-prueba">
            <dt>{t("portada.p3", "Con todo tu placard")}</dt>
            <dd>
              {t(
                "portada.p3d",
                "Una camisa usada cuarenta veces pero no hace dos meses le gana a una que usaste dos veces el miércoles."
              )}
            </dd>
          </div>

          <div className="portada-prueba">
            <dt>{t("portada.p4", "Y te empuja a no comprar")}</dt>
            <dd>
              {t(
                "portada.p4d",
                "«Qué comprar» tiene cinco secciones y las de comprar van al final. Cuatro de las cinco se resuelven sin gastar nada."
              )}
            </dd>
          </div>
        </dl>
      </div>
    </section>
  );
}

export function PortadaPie() {
  return (
    <footer className="portada-pie">
      {/* `alt=""` y `aria-hidden`: es la MISMA marca que ya está en la
          barra, y con texto alternativo un lector de pantalla anuncia
          la página dos veces. Eso no informa, estorba. Misma regla que
          el logotipo del pie en App.js. */}
      <img src="/logo-brujel.png" alt="" aria-hidden="true" width="640" height="348" />
      <span>
        {t("portada.pieNota", "Tu placard, en números. Cero horóscopo de moda.")}
      </span>
    </footer>
  );
}
